// 發售日與精度：解析、正規化字串、排序鍵、分組、已發售與疑似過期判定。
import { daysInMonth, epochDay, fromEpochDay } from './time.ts';

export type Precision = 'day' | 'month' | 'quarter' | 'year' | 'tba';

export type ReleaseDate =
  | { p: 'day'; y: number; m: number; d: number }
  | { p: 'month'; y: number; m: number }
  | { p: 'quarter'; y: number; q: 1 | 2 | 3 | 4 }
  | { p: 'year'; y: number }
  | { p: 'tba' };

/** 精度由精確到模糊的順序；同一個排序鍵時，精確的排前面。 */
export const PRECISION_RANK: Record<Precision, number> = { day: 0, month: 1, quarter: 2, year: 3, tba: 4 };

/** TBA 的排序鍵：大於任何實際日期（約西元 29000 年）。 */
export const TBA_SORT_KEY = 9_999_999;

export const TBA: ReleaseDate = { p: 'tba' };

const MIN_YEAR = 1970;
const MAX_YEAR = 2100;

const RE = {
  day: /^(\d{4})-(\d{2})-(\d{2})$/,
  month: /^(\d{4})-(\d{2})$/,
  quarter: /^(\d{4})-Q([1-4])$/,
  year: /^(\d{4})$/,
};

const validYear = (y: number) => y >= MIN_YEAR && y <= MAX_YEAR;

/** 解析正規化字串：'2026-10-23' | '2026-10' | '2026-Q4' | '2026' | 'TBA'。不合法時回傳 null。 */
export function parseReleaseDate(input: string | null | undefined): ReleaseDate | null {
  if (input == null) return null;
  const s = input.trim();
  if (s.toUpperCase() === 'TBA') return TBA;
  let m = RE.day.exec(s);
  if (m) {
    const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
    return validYear(y) && mo >= 1 && mo <= 12 && d >= 1 && d <= daysInMonth(y, mo) ? { p: 'day', y, m: mo, d } : null;
  }
  m = RE.month.exec(s);
  if (m) {
    const [y, mo] = [Number(m[1]), Number(m[2])];
    return validYear(y) && mo >= 1 && mo <= 12 ? { p: 'month', y, m: mo } : null;
  }
  m = RE.quarter.exec(s);
  if (m) {
    const y = Number(m[1]);
    return validYear(y) ? { p: 'quarter', y, q: Number(m[2]) as 1 | 2 | 3 | 4 } : null;
  }
  m = RE.year.exec(s);
  if (m) {
    const y = Number(m[1]);
    return validYear(y) ? { p: 'year', y } : null;
  }
  return null;
}

const pad = (n: number) => String(n).padStart(2, '0');

/** 轉回正規化字串（parseReleaseDate 的反函式）。 */
export function formatReleaseDate(r: ReleaseDate): string {
  switch (r.p) {
    case 'day':
      return `${r.y}-${pad(r.m)}-${pad(r.d)}`;
    case 'month':
      return `${r.y}-${pad(r.m)}`;
    case 'quarter':
      return `${r.y}-Q${r.q}`;
    case 'year':
      return String(r.y);
    case 'tba':
      return 'TBA';
  }
}

/** 這個發售日涵蓋的日期範圍（epoch day，含頭尾）。TBA 回傳 null。 */
export function releaseRange(r: ReleaseDate): { start: number; end: number } | null {
  switch (r.p) {
    case 'day': {
      const d = epochDay(r.y, r.m, r.d);
      return { start: d, end: d };
    }
    case 'month':
      return { start: epochDay(r.y, r.m, 1), end: epochDay(r.y, r.m, daysInMonth(r.y, r.m)) };
    case 'quarter': {
      const first = (r.q - 1) * 3 + 1;
      return { start: epochDay(r.y, first, 1), end: epochDay(r.y, first + 2, daysInMonth(r.y, first + 2)) };
    }
    case 'year':
      return { start: epochDay(r.y, 1, 1), end: epochDay(r.y, 12, 31) };
    case 'tba':
      return null;
  }
}

/**
 * 排序鍵 = 範圍的「結束日」。所以「2026-10」排在所有 10 月具體日期之後，TBA 排最後。
 * 同一個排序鍵時，再用 PRECISION_RANK 讓精確的排前面（見 compareReleaseDate）。
 */
export function releaseSortKey(r: ReleaseDate): number {
  return releaseRange(r)?.end ?? TBA_SORT_KEY;
}

export function compareReleaseDate(a: ReleaseDate, b: ReleaseDate): number {
  return releaseSortKey(a) - releaseSortKey(b) || PRECISION_RANK[a.p] - PRECISION_RANK[b.p];
}

/** 精度較高者（例如 2026-10-23 相對於 2026）。 */
export function isMorePrecise(a: ReleaseDate, b: ReleaseDate): boolean {
  return PRECISION_RANK[a.p] < PRECISION_RANK[b.p];
}

/**
 * 清單分組。分組順序與 compareReleaseDate 一致：
 * 10 月（具體日期）→ 10 月（日期未定）→ … → Q4 → 2026（日期未定）→ TBA。
 */
export type ReleaseGroup =
  | { kind: 'month'; y: number; m: number; exact: boolean }
  | { kind: 'quarter'; y: number; q: 1 | 2 | 3 | 4 }
  | { kind: 'year'; y: number }
  | { kind: 'tba' };

export function releaseGroup(r: ReleaseDate): ReleaseGroup {
  switch (r.p) {
    case 'day':
      return { kind: 'month', y: r.y, m: r.m, exact: true };
    case 'month':
      return { kind: 'month', y: r.y, m: r.m, exact: false };
    case 'quarter':
      return { kind: 'quarter', y: r.y, q: r.q };
    case 'year':
      return { kind: 'year', y: r.y };
    case 'tba':
      return { kind: 'tba' };
  }
}

export function releaseGroupKey(g: ReleaseGroup): string {
  switch (g.kind) {
    case 'month':
      return `${g.y}-${pad(g.m)}${g.exact ? '' : '~'}`;
    case 'quarter':
      return `${g.y}-Q${g.q}`;
    case 'year':
      return `${g.y}~`;
    case 'tba':
      return 'TBA';
  }
}

/** 遊戲狀態（數值會寫進快照，只能新增、不能改號）。 */
export const GameStatus = {
  announced: 0,
  earlyAccess: 1,
  released: 2,
  cancelled: 3,
} as const;
export type GameStatus = (typeof GameStatus)[keyof typeof GameStatus];

/**
 * 是否已發售（以台灣日期判斷）：
 * - 已取消 → 否
 * - 整個期間都還沒到（例如 2026-10-27、2026-Q4）→ 否，不論來源怎麼標示。
 *   來源常把「搶先體驗中、之後才正式發售」的遊戲標成已發售或搶先體驗，但這裡顯示的是正式版的日期（GDO-23）
 * - 來源明確標示已發售或搶先體驗 → 是
 * - 精度為「日」且日期 ≤ 今天 → 是
 * - 其他（含不明確日期）→ 否；不明確日期是否過期由 isPossiblyStale 判斷
 */
export function isReleased(r: ReleaseDate, status: GameStatus, todayTaipei: number): boolean {
  if (status === GameStatus.cancelled) return false;
  const range = releaseRange(r);
  if (range && range.start > todayTaipei) return false;
  if (status === GameStatus.released || status === GameStatus.earlyAccess) return true;
  return r.p === 'day' && releaseSortKey(r) <= todayTaipei;
}

/** 期間已經過去卻還沒發售（例如現在是 Q3，資料還寫「2026 Q2」）→ 資料可能過期或已延期。 */
export function isPossiblyStale(r: ReleaseDate, status: GameStatus, todayTaipei: number): boolean {
  if (r.p === 'day' || r.p === 'tba') return false;
  if (status !== GameStatus.announced) return false;
  return releaseSortKey(r) < todayTaipei;
}

/**
 * 過期很久（GDO-23）：不明確的日期在「上一季開始之前」就已經結束（例如 2026-09 時的 2025、2026-Q1、2026-03）。
 * 這些遊戲大多已延期或資料來源沒有更新，不放在未發售的主要時間軸，改列在最後的「可能過期」。
 * 剛過期一季以內的（2026-Q2、2026-08）仍留在原本的位置，只標示「資料可能過期」。
 */
export function isLongOverdue(r: ReleaseDate, status: GameStatus, todayTaipei: number): boolean {
  if (!isPossiblyStale(r, status, todayTaipei)) return false;
  const { y, m } = fromEpochDay(todayTaipei);
  const quarterFirstMonth = Math.floor((m - 1) / 3) * 3 + 1;
  const previous = quarterFirstMonth === 1 ? epochDay(y - 1, 10, 1) : epochDay(y, quarterFirstMonth - 3, 1);
  return releaseSortKey(r) < previous;
}
